// src/utils/RuleUtils.js

/**
 * Utilitaires communs pour les règles de validation
 */
export class RuleUtils {
  /**
   * Formater l'ID d'une fibre pour l'affichage
   * @param {string} fiberId - ID de la fibre (ex: "T1-A-1")
   * @returns {string} ID formaté (ex: "T1-MA-01")
   */
  static formatFiberId(fiberId) {
    if (!fiberId || typeof fiberId !== "string") {
      return fiberId || "ID-INVALIDE";
    }

    const parts = fiberId.split("-");
    if (parts.length !== 3) {
      return fiberId;
    }

    const [tiroir, module, port] = parts;
    const formattedPort = port.padStart(3, "0");
    return `${tiroir}-M${module}-${formattedPort}`;
  }

  /**
   * Extraire la position depuis le nom de fichier
   * @param {string} fileName - Nom du fichier
   * @returns {Object} Position extraite ou null
   */
  static extractPositionFromName(fileName) {
    // Regex pour extraire la position B2-T[X]-M[Y]-[ZZZ] du nom
    const regex = /_(B2-T(\d)-M([A-Z])-(\d{3}))_OE_(\d{4})/;
    const match = fileName.match(regex);

    if (!match) {
      return null;
    }

    const [, fullPosition, tiroir, module, fibre, wavelength] = match;

    return {
      fullPosition: fullPosition,
      tiroir: tiroir,
      module: module,
      fibre: fibre,
      wavelength: parseInt(wavelength),
    };
  }

  /**
   * Extraire la structure B2-T?-M?-??? depuis un cable ID
   * @param {string} cableId - Cable ID
   * @returns {string|null} Structure extraite ou null
   */
  static extractB2Structure(cableId) {
    if (!cableId || typeof cableId !== "string") {
      return null;
    }

    // Regex pour B2-T[0-9]-M[A-Z]-[000-024]
    const regex = /(B2-T\d-M[A-Z]-(0[0-1]\d|02[0-4]))/;
    const match = cableId.match(regex);

    return match ? match[1] : null;
  }

  /**
   * Vérifier si une fibre est libre
   * @param {Object} fiber - Objet fibre
   * @returns {boolean} true si la fibre est libre
   */
  static isFiberFree(fiber) {
    if (!fiber.etat) return false;

    const etatLower = fiber.etat.toLowerCase().trim();
    return etatLower === "libre";
  }

  /**
   * Extraire et analyser tous les éléments d'un nom de fichier SOR selon le standard
   * Format attendu: [PREFIX]_B2-T[X]-M[Y]-[ZZZ]_OE_[WAVELENGTH][OPTIONAL_SUFFIX].sor
   *
   * @param {string} fileName - Nom complet du fichier à analyser (ex: "SRO-BPI-11427815_PBO101_B2-T1-MA-001_OE_1310-2.sor")
   * @returns {Object} Objet contenant tous les éléments extraits et leur statut de validation
   */
  static parseSorFileName(fileName) {
    const elements = {
      fileName: fileName,
      prefix: null,
      position: null,
      tiroir: null,
      module: null,
      fibre: null,
      wavelength: null,
      suffix: null,
      hasValidExtension: false,
      hasValidPosition: false,
      hasValidWavelength: false,
      hasValidSuffix: true, // Par défaut true car optionnel
    };

    // Vérifier l'extension
    if (fileName.endsWith(".sor")) {
      elements.hasValidExtension = true;
    }

    // Enlever l'extension pour analyse
    const nameWithoutExt = fileName.replace(/\.sor$/, "");

    // Vérifier et extraire autour de _OE_
    if (nameWithoutExt.includes("_OE_")) {
      const parts = nameWithoutExt.split("_OE_");

      if (parts.length === 2) {
        const [leftPart, rightPart] = parts;

        // Extraire longueur d'onde et suffixe
        const wavelengthMatch = rightPart.match(/^(1310|1550)([-_]\d)?$/);
        if (wavelengthMatch) {
          elements.wavelength = parseInt(wavelengthMatch[1]);
          elements.suffix = wavelengthMatch[2] || null;
          elements.hasValidWavelength = true;
          elements.hasValidSuffix = wavelengthMatch[2] ? wavelengthMatch[2].match(/^[-_]\d$/) !== null : true;
        } else {
          // Tenter d'extraire au moins la longueur d'onde
          const waveMatch = rightPart.match(/^(1310|1550)/);
          if (waveMatch) {
            elements.wavelength = parseInt(waveMatch[1]);
            elements.hasValidWavelength = true;
            elements.hasValidSuffix = false; // Suffixe mal formaté
          }
        }

        // Extraire prefix et position
        const positionMatch = leftPart.match(/^(.+)_(B2-T(\d)-M([A-Z])-(\d{3}))$/);
        if (positionMatch) {
          elements.prefix = positionMatch[1];
          elements.position = positionMatch[2];
          elements.tiroir = positionMatch[3];
          elements.module = positionMatch[4];
          elements.fibre = positionMatch[5];
          elements.hasValidPosition = true;
        } else {
          // Tenter d'extraire le prefix même si position invalide
          const lastUnderscore = leftPart.lastIndexOf("_");
          if (lastUnderscore > 0) {
            elements.prefix = leftPart.substring(0, lastUnderscore);
          }
        }
      }
    } else {
      // Pas de _OE_, mais tenter d'extraire prefix si possible
      const underscores = nameWithoutExt.split("_");
      if (underscores.length > 1) {
        elements.prefix = underscores[0];
      }
    }

    return elements;
  }

  /**
   * Générer la structure B2 attendue depuis un fiber ID
   * @param {string} fiberId - ID de la fibre
   * @returns {string|null} Structure B2 attendue ou null
   */
  static generateExpectedB2Structure(fiberId) {
    const formattedId = RuleUtils.formatFiberId(fiberId);
    if (formattedId === fiberId || formattedId === "ID-INVALIDE") {
      return null;
    }

    return `B2-${formattedId}`;
  }

  /**
   * Extraire la position depuis les métadonnées
   * @param {Object} metadata - Métadonnées du fichier
   * @returns {Object} Position attendue ou null
   */
  static extractPositionFromMetadata(metadata) {
    if (!metadata || !metadata.cableId || typeof metadata.numeroFibreOrigine !== "number") {
      return null;
    }

    // Extraire B2-T[X]-M[Y] depuis cableId
    const cableIdMatch = metadata.cableId.match(/(B2-T\d-M[A-Z])$/);
    if (!cableIdMatch) {
      return null;
    }

    const positionBase = cableIdMatch[1];
    const fibreFormatted = metadata.numeroFibreOrigine.toString().padStart(3, "0");

    return {
      fullPosition: `${positionBase}-${fibreFormatted}`,
      positionBase: positionBase,
      fibre: fibreFormatted,
      wavelength: metadata.wavelength,
    };
  }
}
